import React from "react";
import { ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import EditableDataDisplay from "./EditableDataDisplay";

const StatusSection = ({
  isDarkMode,
  themeClasses,
  statuses,
  expandedTasks,
  templateNames,
  resultViewMode,
  editingDocuments,
  toggleExpand,
  formatDate,
  getStatusColor,
  copyToClipboard,
  downloadAsCSV,
  toggleResultViewMode,
  handleEditDocument,
  handleCancelEdit,
}) => {
  if (statuses.length === 0) {
    return (
      <p
        className={`text-gray-500 ${
          isDarkMode ? "text-gray-400" : "text-gray-600"
        } font-body`}
      >
        No tasks in progress.
      </p>
    );
  }

  return (
    <ul className="space-y-4">
      {statuses.map((status, index) => {
        // Use the cached template name or fall back to status.template_name
        const templateName =
          templateNames[status.template_id] ||
          status.template_name ||
          "Loading...";

        return (
          <li
            key={index}
            className={`rounded-lg border overflow-hidden shadow-sm transition-all duration-300 hover:shadow-md ${
              isDarkMode
                ? "bg-gray-700 border-slate-950"
                : "bg-gray-100 border-slate-280"
            }`}
          >
            <div
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 cursor-pointer"
              onClick={() => toggleExpand(status.task_id)}
            >
              <div className="flex items-center space-x-4">
                <div
                  className={`w-3 h-3 rounded-full ${
                    status.state === "SUCCESS"
                      ? "bg-green-500"
                      : status.state === "FAILURE"
                      ? "bg-red-500"
                      : "bg-yellow-500"
                  }`}
                ></div>
                <div>
                  <span
                    className={`font-medium ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    {templateName}
                  </span>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {formatDate(status.date)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                    status.state
                  )}`}
                >
                  {status.state}
                </span>
                <div
                  className={`transition-transform duration-300 ${
                    expandedTasks[status.task_id] ? "rotate-180" : ""
                  }`}
                >
                  <ChevronDown
                    size={16}
                    className={themeClasses.subtext}
                  />
                </div>
              </div>
            </div>

            {expandedTasks[status.task_id] && (
              <div
                className={`p-4 border-t ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-800"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                {/* View Toggle */}
                <div className="flex justify-end mb-2 space-x-2">
                  <button
                    onClick={() =>
                      copyToClipboard(status.result.gpt4_analysis)
                    }
                    className={`px-2 py-1 text-xs rounded flex items-center ${
                      isDarkMode
                        ? "bg-gray-600 hover:bg-gray-500 text-gray-200"
                        : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                    }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-3.5 h-3.5 mr-1"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect
                        x="9"
                        y="9"
                        width="13"
                        height="13"
                        rx="2"
                        ry="2"
                      ></rect>
                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
                    </svg>
                    Copy
                  </button>

                  <button
                    onClick={() =>
                      downloadAsCSV(status.result.gpt4_analysis)
                    }
                    className={`px-2 py-1 text-xs rounded flex items-center ${
                      isDarkMode
                        ? "bg-green-600/30 hover:bg-green-500/40 text-green-300"
                        : "bg-green-100 hover:bg-green-200 text-green-700"
                    }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-3.5 h-3.5 mr-1"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    CSV
                  </button>

                  <button
                    onClick={() => toggleResultViewMode(status.task_id)}
                    className={`px-2 py-1 text-xs rounded ${
                      isDarkMode
                        ? "bg-gray-600 hover:bg-gray-500 text-gray-200"
                        : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                    }`}
                  >
                    Switch to{" "}
                    {resultViewMode[status.task_id] === "json"
                      ? "Formatted"
                      : "JSON"}{" "}
                    View
                  </button>
                </div>

                {/* Conditional Rendering */}
                {resultViewMode[status.task_id] === "json" ? (
                  <>
                    {/* Display GPT Analysis as JSON */}
                    {status.result?.gpt4_analysis && (
                      <div className="mb-4">
                        <h4
                          className={`text-sm font-medium mb-1 ${themeClasses.subtext}`}
                        >
                          AI Analysis (JSON):
                        </h4>
                        <pre
                          className={`whitespace-pre-wrap text-xs p-3 rounded ${
                            isDarkMode
                              ? "bg-black/30 text-gray-300"
                              : "bg-gray-100 text-gray-800"
                          } overflow-x-auto`}
                        >
                          {(() => {
                            try {
                              let dataToDisplay = status.result.gpt4_analysis;
                              if (typeof dataToDisplay === "string") {
                                let cleanedData = dataToDisplay.trim();
                                if (cleanedData.startsWith("```")) {
                                  const match = cleanedData.match(
                                    /^```(?:json)?\s*([\s\S]*?)\s*```$/
                                  );
                                  if (match) cleanedData = match[1];
                                }
                                dataToDisplay = JSON.parse(cleanedData);
                              }
                              return JSON.stringify(dataToDisplay, null, 2);
                            } catch (e) {
                              return status.result.gpt4_analysis;
                            }
                          })()}
                        </pre>
                      </div>
                    )}
                    {/* Display Integration Result as JSON */}
                    {status.result?.integration_result && (
                      <div>
                        <h4
                          className={`text-sm font-medium mb-1 ${themeClasses.subtext}`}
                        >
                          Integration Result (JSON):
                        </h4>
                        <pre
                          className={`whitespace-pre-wrap text-xs p-3 rounded ${
                            isDarkMode
                              ? "bg-black/30 text-gray-300"
                              : "bg-gray-100 text-gray-800"
                          } overflow-x-auto`}
                        >
                          {(() => {
                            try {
                              return JSON.stringify(
                                status.result.integration_result,
                                null,
                                2
                              );
                            } catch (e) {
                              return String(status.result.integration_result);
                            }
                          })()}
                        </pre>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Display GPT Analysis using EditableDataDisplay */}
                    {status.result?.gpt4_analysis && (
                      <div className="mb-4">
                        <h4
                          className={`text-sm font-medium mb-2 ${themeClasses.subtext}`}
                        >
                          AI Analysis:
                        </h4>
                        <EditableDataDisplay
                          data={status.result.gpt4_analysis}
                          themeClasses={themeClasses}
                          isDarkMode={isDarkMode}
                          isEditing={
                            editingDocuments[status.task_id] || false
                          }
                          onEdit={() => handleEditDocument(status.task_id)}
                          onSave={(updatedData) => {
                            // For status section, we might want different handling
                            // For now, just show a message
                            toast.info(
                              "Editing task results is not supported in status section. Please wait for the task to complete and edit from history."
                            );
                            handleCancelEdit(status.task_id);
                          }}
                          onCancel={() => handleCancelEdit(status.task_id)}
                        />
                      </div>
                    )}
                    {/* Display Integration Result using EditableDataDisplay */}
                    {status.result?.integration_result && (
                      <div>
                        <h4
                          className={`text-sm font-medium mb-2 ${themeClasses.subtext}`}
                        >
                          Integration Result:
                        </h4>
                        <EditableDataDisplay
                          data={status.result.integration_result}
                          themeClasses={themeClasses}
                          isDarkMode={isDarkMode}
                          isEditing={
                            editingDocuments[
                              `${status.task_id}_integration`
                            ] || false
                          }
                          onEdit={() =>
                            handleEditDocument(
                              `${status.task_id}_integration`
                            )
                          }
                          onSave={(updatedData) => {
                            // For status section, we might want different handling
                            toast.info(
                              "Editing task results is not supported in status section. Please wait for the task to complete and edit from history."
                            );
                            handleCancelEdit(
                              `${status.task_id}_integration`
                            );
                          }}
                          onCancel={() =>
                            handleCancelEdit(
                              `${status.task_id}_integration`
                            )
                          }
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Display error if analysis failed */}
                {status.state === "FAILURE" && status.error && (
                  <div
                    className={`mt-2 p-2 rounded border ${
                      isDarkMode
                        ? "bg-red-900/30 border-red-700 text-red-300"
                        : "bg-red-100 border-red-300 text-red-700"
                    } text-xs`}
                  >
                    Error: {status.error}
                  </div>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default StatusSection;
